/**
 * URL <-> chef date-range plumbing, kept pure so it's unit-testable without a
 * DOM (this client has no RTL / renderHook — see `hooks/chefs/useChefDateRange`
 * for the thin React glue that consumes these).
 *
 * The chef's range lives in three places, in precedence order:
 *
 *   1. the URL (`?date=YYYY-MM-DD&view=daily|weekly|custom[&end=YYYY-MM-DD]`) —
 *      a deep link or a Back navigation to an entry that carried params,
 *   2. the sessionStorage-backed store (`stores/chefs/date-range`) — what keeps
 *      the range across an orders <-> shopping-list flip, since the sidebar
 *      navigates with bare hrefs and carries no search string,
 *   3. the surface's own cold-start default.
 *
 * `resolveDateRange` is that precedence chain, and it runs on EVERY render
 * rather than seeding state once at mount — a `replace: true` write leaves no
 * history entry, but real entries carrying params exist, and a mount-only read
 * would leave the URL and the UI permanently disagreeing after a Back.
 *
 * `serializeDateRange` / `deserializeDateRange` are the only conversions
 * between the `Date`-carrying `DateRange` the pages render and the string form
 * the URL and the store hold, so a custom window's end can't be dropped on one
 * leg of the round trip.
 */

import {
  customRangeFromKeys,
  type DateGranularity,
  type DateRange,
  isoDate,
  isValidDateKey,
  localDateFromKey,
  rangeForGranularityToggle,
} from './service-days'

export const PARAM_DATE = 'date'
export const PARAM_VIEW = 'view'
export const PARAM_END = 'end'

/**
 * A range in its serialized form — keys are local `YYYY-MM-DD`. Mirrors the
 * `DateRange` union so a custom window is unrepresentable without its end.
 */
export type SerializedDateRange =
  | { granularity: 'daily' | 'weekly'; anchorKey: string }
  | { granularity: 'custom'; anchorKey: string; endKey: string }

/** A stored range, day-stamped with the date it was chosen on. */
export type StoredDateRange = SerializedDateRange & { seededOn: string }

const GRANULARITIES: readonly DateGranularity[] = ['daily', 'weekly', 'custom']

export function serializeDateRange(range: DateRange): SerializedDateRange {
  return range.granularity === 'custom'
    ? { granularity: 'custom', anchorKey: isoDate(range.anchor), endKey: isoDate(range.end) }
    : { granularity: range.granularity, anchorKey: isoDate(range.anchor) }
}

export function deserializeDateRange(serialized: SerializedDateRange): DateRange {
  return serialized.granularity === 'custom'
    ? {
        granularity: 'custom',
        anchor: localDateFromKey(serialized.anchorKey),
        end: localDateFromKey(serialized.endKey),
      }
    : { granularity: serialized.granularity, anchor: localDateFromKey(serialized.anchorKey) }
}

/**
 * Read a range off the URL. Returns `null` unless the params the named view
 * needs are ALL present and valid — a half-specified range is ambiguous (which
 * half wins?), so it's treated as absent and the store/default takes over. For
 * `view=custom` that includes `end`: a custom view with no end, or one that
 * ends before it starts, names no window.
 */
export function parseDateRangeParams(
  dateParam: string | null,
  viewParam: string | null,
  endParam: string | null,
): SerializedDateRange | null {
  if (!dateParam || !viewParam) {
    return null
  }
  if (!isValidDateKey(dateParam)) {
    return null
  }
  const granularity = GRANULARITIES.find((g) => g === viewParam)
  if (!granularity) {
    return null
  }
  if (granularity !== 'custom') {
    return { granularity, anchorKey: dateParam }
  }
  // Clamped, not rejected, when the link asks for more days than the picker
  // allows — a hand-edited or stale link lands on the longest legal window.
  const clamped = endParam ? customRangeFromKeys(dateParam, endParam) : null
  return clamped ? serializeDateRange(clamped) : null
}

/**
 * Write the range into a COPY of the existing params, so unrelated params on
 * these routes survive — `?select` and `?orderItemId` on the kitchen page,
 * `?tab` on the shopping list. Building a fresh `URLSearchParams` from just the
 * range would silently drop them.
 */
export function writeDateRangeParams(prev: URLSearchParams, next: SerializedDateRange): URLSearchParams {
  const params = new URLSearchParams(prev)
  params.set(PARAM_DATE, next.anchorKey)
  params.set(PARAM_VIEW, next.granularity)
  if (next.granularity === 'custom') {
    params.set(PARAM_END, next.endKey)
    return params
  }
  // Dropped rather than left behind: a leftover `end` from a previous custom
  // window would silently re-apply the moment the chef switched back.
  params.delete(PARAM_END)
  return params
}

/**
 * URL wins, else a store entry chosen TODAY, else the surface's default.
 *
 * The `seededOn` check is the midnight guard. Each page currently recomputes
 * `today` on mount and navigation remounts it, so an overnight-open tab
 * self-corrects today. Persisting to sessionStorage would freeze the anchor for
 * the tab's whole lifetime: a chef returning Tuesday morning to a tab left open
 * Monday would still be on Monday, under a header now reading "Yesterday".
 * A store entry from a previous day is therefore treated as absent.
 */
export function resolveDateRange(args: {
  fromUrl: SerializedDateRange | null
  stored: StoredDateRange | null
  defaultGranularity: DateGranularity
  today: Date
}): SerializedDateRange {
  const { fromUrl, stored, defaultGranularity, today } = args
  if (fromUrl) {
    return fromUrl
  }
  if (stored && stored.seededOn === isoDate(today)) {
    return stored.granularity === 'custom'
      ? { granularity: 'custom', anchorKey: stored.anchorKey, endKey: stored.endKey }
      : { granularity: stored.granularity, anchorKey: stored.anchorKey }
  }
  return serializeDateRange(rangeForGranularityToggle(defaultGranularity, today))
}
